import { useEffect, useMemo } from 'react'
import { toast } from 'sonner'

import { Combobox } from '@/components/ui/combobox'
import { Label } from '@/components/ui/label'
import { useProvinces, useWards } from '@/hooks/use-provinces-query'
import { m } from '@/lib/paraglide/messages'

export interface ProvinceWardValue {
    provinceCode?: string
    provinceName?: string
    wardCode?: string
    wardName?: string
}

interface Props {
    value: ProvinceWardValue
    onChange: (next: ProvinceWardValue) => void
    disabled?: boolean
}

// Controlled cascading tỉnh → phường picker (post-2025 GSO admin reform: no
// district level). Owns the provinces/wards queries; the parent owns state, so
// it works under both react-hook-form (watch/setValue) and useState callers.
// Stores both the GSO code and the display name; legacy rows may have a name
// with no code, in which case the stored name is surfaced as a hint.
export function ProvinceWardPicker({ value, onChange, disabled }: Props) {
    const provincesQuery = useProvinces()
    const wardsQuery = useWards(value.provinceCode || undefined)

    const provinceOptions = useMemo(
        () =>
            (provincesQuery.data ?? []).map((p) => ({
                value: p.code,
                label: p.name,
            })),
        [provincesQuery.data],
    )

    const wardOptions = useMemo(
        () =>
            (wardsQuery.data ?? []).map((w) => ({
                value: w.code,
                label: w.name,
            })),
        [wardsQuery.data],
    )

    useEffect(() => {
        if (provincesQuery.isError || wardsQuery.isError) {
            toast.error(m.customer_provinces_load_failed())
        }
    }, [provincesQuery.isError, wardsQuery.isError])

    const provincePlaceholder =
        value.provinceName && !value.provinceCode
            ? `${value.provinceName} (${m.customer_legacy_admin_unit_hint()})`
            : m.customer_province_placeholder()

    const wardPlaceholder = !value.provinceCode
        ? m.customer_pick_province_first()
        : value.wardName && !value.wardCode
          ? `${value.wardName} (${m.customer_legacy_admin_unit_hint()})`
          : m.customer_ward_placeholder()

    return (
        <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
                <Label>{m.customer_province_label()}</Label>
                <Combobox
                    options={provinceOptions}
                    value={value.provinceCode || undefined}
                    onChange={(code) => {
                        const picked = provincesQuery.data?.find(
                            (p) => p.code === code,
                        )
                        // Switching province invalidates any chosen ward.
                        onChange({
                            provinceCode: code,
                            provinceName: picked?.name ?? '',
                            wardCode: '',
                            wardName: '',
                        })
                    }}
                    placeholder={provincePlaceholder}
                    searchPlaceholder={m.customer_province_search_placeholder()}
                    emptyText={m.customer_no_results()}
                    loading={provincesQuery.isLoading}
                    disabled={disabled}
                />
            </div>
            <div className="space-y-1.5">
                <Label>{m.customer_ward_label()}</Label>
                <Combobox
                    options={wardOptions}
                    value={value.wardCode || undefined}
                    onChange={(code) => {
                        const picked = wardsQuery.data?.find(
                            (w) => w.code === code,
                        )
                        onChange({
                            ...value,
                            wardCode: code,
                            wardName: picked?.name ?? '',
                        })
                    }}
                    placeholder={wardPlaceholder}
                    searchPlaceholder={m.customer_ward_search_placeholder()}
                    emptyText={m.customer_no_results()}
                    disabled={disabled || !value.provinceCode}
                    loading={wardsQuery.isLoading}
                />
            </div>
        </div>
    )
}
